import React, { useRef, useState, useEffect } from "react";
import { Camera, UploadCloud, X, Edit2, CheckCircle } from "lucide-react";
import SafetyCameraCaptureModal from "../Safety/Safety_Inspection/SafetyCameraCaptureModal";
import SafetyImageAnnotationModal from "../Safety/Safety_Inspection/SafetyImageAnnotationModal";

const dataUrlToFile = (dataUrl, filename = "annotated-image.png") => {
    const [meta, base64] = dataUrl.split(",");
    const mime = meta.match(/:(.*?);/)?.[1] || "image/png";
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
        array[i] = binary.charCodeAt(i);
    }

    return new File([array], filename, { type: mime });
};

const blobToFile = (blob, filename = "annotated-image.png") => {
    if (blob instanceof File) return blob;
    return new File([blob], filename, { type: blob.type || "image/png" });
};

const normalizeToFile = async (payload, fallbackName = "annotated-image.png") => {
    if (!payload) return null;

    if (payload instanceof File) return payload;

    if (payload instanceof Blob) {
        return blobToFile(payload, fallbackName);
    }

    if (typeof payload === "string" && payload.startsWith("data:")) {
        return dataUrlToFile(payload, fallbackName);
    }

    if (payload?.file instanceof File) {
        return payload.file;
    }

    if (payload?.dataUrl) {
        return dataUrlToFile(payload.dataUrl, fallbackName);
    }

    if (payload?.blob) {
        return blobToFile(payload.blob, fallbackName);
    }

    return null;
};

export default function NCRAnnotatableImageUpload({
    value = [],
    onChange,
    label,
    testId,
    multiple = true,
    accept = "image/*"
}) {
    const fileInputRef = useRef(null);

    const [cameraOpen, setCameraOpen] = useState(false);
    const [annotationOpen, setAnnotationOpen] = useState(false);
    const [pendingAnnotationIndex, setPendingAnnotationIndex] = useState(null);
    const [pendingAnnotationFile, setPendingAnnotationFile] = useState(null);

    // Cleanup object URLs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            value.forEach(item => {
                if (item.preview && item.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(item.preview);
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only on unmount, active cleanup happens in handlers

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const newItems = files.map((file, index) => ({
            id: `${Date.now()}-${index}-${file.name}`,
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isAnnotated: false
        }));

        const updatedValue = [...value, ...newItems];
        onChange(updatedValue);
        
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        // Open annotation for the FIRST newly added image
        if (newItems.length > 0) {
            const firstNewIndex = value.length;
            setPendingAnnotationIndex(firstNewIndex);
            setPendingAnnotationFile(newItems[0].file);
            setAnnotationOpen(true);
        }
    };

    const handleRemove = (index) => {
        const itemToRemove = value[index];
        if (itemToRemove?.preview && itemToRemove.preview.startsWith('blob:')) {
            URL.revokeObjectURL(itemToRemove.preview);
        }

        const newValue = [...value];
        newValue.splice(index, 1);
        onChange(newValue);
    };

    const handleCameraCapture = async (capturedPayload) => {
        const file = await normalizeToFile(capturedPayload, `captured-${Date.now()}.png`);
        if (!file) return;

        const newItem = {
            id: `${Date.now()}-captured`,
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isAnnotated: false
        };

        const newIndex = value.length;
        onChange([...value, newItem]);
        
        // Open annotation modal for this newly captured image
        setPendingAnnotationIndex(newIndex);
        setPendingAnnotationFile(newItem.file);
        setAnnotationOpen(true);
    };

    const handleManualAnnotate = (index) => {
        setPendingAnnotationIndex(index);
        setPendingAnnotationFile(value[index].file);
        setAnnotationOpen(true);
    };

    const handleAnnotationSave = async (annotatedPayload) => {
        if (pendingAnnotationIndex === null) return;
        
        const originalItem = value[pendingAnnotationIndex];
        const annotatedFile = await normalizeToFile(annotatedPayload, originalItem.name);
        
        if (annotatedFile) {
            const newValue = [...value];
            
            // Cleanup old preview
            if (originalItem.preview && originalItem.preview.startsWith('blob:')) {
                URL.revokeObjectURL(originalItem.preview);
            }

            newValue[pendingAnnotationIndex] = {
                ...originalItem,
                file: annotatedFile,
                preview: URL.createObjectURL(annotatedFile),
                isAnnotated: true
            };
            
            onChange(newValue);
        }
        
        handleAnnotationClose();
    };

    const handleAnnotationClose = () => {
        setAnnotationOpen(false);
        setPendingAnnotationIndex(null);
        setPendingAnnotationFile(null);
    };

    return (
        <div className="w-full">
            {label && (
                <label className="mb-2 block text-sm font-medium leading-none text-slate-700">
                    {label}
                </label>
            )}

            <div className="mb-4 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
                    data-testid={testId ? `${testId}-upload-btn` : undefined}
                >
                    <UploadCloud className="h-4 w-4 text-slate-500" />
                    Upload Image
                </button>
                <button
                    type="button"
                    onClick={() => setCameraOpen(true)}
                    className="flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
                    data-testid={testId ? `${testId}-camera-btn` : undefined}
                >
                    <Camera className="h-4 w-4 text-slate-500" />
                    Take Photo
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept={accept}
                    multiple={multiple}
                    className="hidden"
                    data-testid={testId ? `${testId}-input` : undefined}
                />
            </div>

            {value.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {value.map((item, index) => {
                        const isImage = item.file?.type?.startsWith("image/") || item.name?.match(/\.(jpeg|jpg|gif|png|webp)$/i);
                        return (
                            <div
                                key={item.id}
                                className="group relative flex aspect-square flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                            >
                                {isImage ? (
                                    <img
                                        src={item.preview}
                                        alt={item.name}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center bg-slate-100 p-4 text-center">
                                        <span className="text-xs text-slate-500 break-all">{item.name}</span>
                                    </div>
                                )}
                                
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                                    {isImage && (
                                        <button
                                            type="button"
                                            onClick={() => handleManualAnnotate(index)}
                                            className="flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                                        >
                                            <Edit2 className="h-3 w-3" />
                                            Annotate
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(index)}
                                        className="flex items-center gap-1 rounded bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                                    >
                                        <X className="h-3 w-3" />
                                        Remove
                                    </button>
                                </div>
                                
                                {item.isAnnotated && (
                                    <div className="absolute bottom-1 right-1 flex items-center gap-1 rounded bg-green-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow">
                                        <CheckCircle className="h-3 w-3" />
                                        Annotated
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <SafetyCameraCaptureModal
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handleCameraCapture}
                title="Take Photo"
            />

            <SafetyImageAnnotationModal
                open={annotationOpen}
                file={pendingAnnotationFile}
                onClose={handleAnnotationClose}
                onSave={handleAnnotationSave}
            />
        </div>
    );
}
